import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import { foodAnalyzerService, RecognizeEventResponse, NutritionInfo } from '../../services/food-analyzer.service';
import { toastService } from '../../services/toast.service';
import { generateFoodHtmlReport } from '../../utils/food-report.util';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { localNowIso } from '@/utils/datetime';

interface QuickFoodAddModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  onRefreshDashboard?: () => void;
}

const calculateItemNutrients = (amount: number | undefined, nutrition: NutritionInfo | undefined) => {
  if (!nutrition || amount === undefined) return null;
  const factor = amount / 100;
  return {
    kcal: (nutrition.caloriesPer100 * factor).toFixed(0),
    p: (nutrition.proteinPer100 * factor).toFixed(1),
    f: (nutrition.fatPer100 * factor).toFixed(1),
    c: (nutrition.carbsPer100 * factor).toFixed(1),
    fiber: (nutrition.fiberPer100 * factor).toFixed(1),
  };
};

export const QuickFoodAddModal: React.FC<QuickFoodAddModalProps> = ({ 
  open, 
  onClose, 
  selectedDate,
  onRefreshDashboard 
}) => {
  const [expertMode, setExpertMode] = useState(() => localStorage.getItem('food-modal-expert-mode') === 'true');
  const [inputText, setInputText] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeEventResponse[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleClose = () => {
    setInputText('');
    setRecognizedEvents([]);
    setIsRecognizing(false);
    setIsEstimating(false);
    setEditingIndex(null);
    onClose();
  };

  const handleRecognize = async () => {
    if (!inputText.trim()) return;

    setIsRecognizing(true);
    setEditingIndex(null);
    try {
      const now = new Date();
      const [year, month, day] = selectedDate.split('-').map(Number);
      const contextDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());

      const data = await foodAnalyzerService.recognize({
        text: inputText,
        contextDate: contextDate.toISOString()
      });
      setRecognizedEvents(data.items);
    } catch (error: any) {
      console.error('Recognition error:', error);
      toastService.error(error?.message || 'Не вдалося розпізнати текст. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleEstimateNutrition = async () => {
    if (recognizedEvents.length === 0) return;

    setIsEstimating(true);
    try {
      const items = recognizedEvents.map(event => ({
        name: event.name || 'Без назви',
        totalAmount: Number(event.totalAmount) || 0,
        unit: event.unit || 'г'
      }));

      const data = await foodAnalyzerService.estimateNutrition({ items });
      
      const updatedEvents = recognizedEvents.map((event, index) => {
        if (data[index] && data[index].nutrition) {
          return { ...event, nutrition: data[index].nutrition };
        }
        return event;
      });

      setRecognizedEvents(updatedEvents);
      toastService.success('Нутрієнти успішно розраховано');
    } catch (error: any) {
      console.error('Nutrition estimation error:', error);
      toastService.error(error?.message || 'Не вдалося розрахувати нутрієнти.');
    } finally {
      setIsEstimating(false);
    }
  };

  const totals = useMemo(() => {
    return recognizedEvents.reduce((acc, event) => {
      if (event.nutrition && event.totalAmount !== undefined) {
        const factor = Number(event.totalAmount) / 100;
        acc.calories += (Number(event.nutrition.caloriesPer100) || 0) * factor;
        acc.protein += (Number(event.nutrition.proteinPer100) || 0) * factor;
        acc.fat += (Number(event.nutrition.fatPer100) || 0) * factor;
        acc.carbs += (Number(event.nutrition.carbsPer100) || 0) * factor;
        acc.fiber += (Number(event.nutrition.fiberPer100) || 0) * factor;
      }
      return acc;
    }, { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0 });
  }, [recognizedEvents]);

  const handleAddItemManual = () => {
    const newItem: RecognizeEventResponse = {
      type: 'food',
      name: '',
      unit: 'г',
      portionCount: 1,
      portionSize: 0,
      totalAmount: 0,
      scheduledLdt: localNowIso(),
    };
    const newList = [...recognizedEvents, newItem];
    setRecognizedEvents(newList);
    setEditingIndex(newList.length - 1);
  };

  const handleDeleteItem = (index: number) => {
    setRecognizedEvents(recognizedEvents.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
  };

  const handleGenerateReport = () => {
    const reportHtml = generateFoodHtmlReport(selectedDate, inputText, recognizedEvents, totals);
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(reportHtml);
      newWindow.document.close();
    } else {
      toastService.error('Не вдалося відкрити нове вікно. Дозвольте спливаючі вікна.');
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'food': return <RestaurantIcon sx={{ color: '#6EB52C' }} />;
      case 'liquid': return <WaterDropIcon sx={{ color: '#41AEE4' }} />;
      case 'workout': return <FitnessCenterIcon sx={{ color: '#FF9800' }} />;
      case 'unrecognized': return <QuestionMarkIcon sx={{ color: '#A8A8A8' }} />;
      default: return <AutoFixHighIcon />;
    }
  };

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeEventResponse, value: any) => {
    const updatedEvents = [...recognizedEvents];
    const updatedItem = { ...updatedEvents[index], [field]: value };
    
    if (field === 'portionSize' || field === 'portionCount') {
        const size = Number(updatedItem.portionSize || 0);
        const count = Number(updatedItem.portionCount || 0);
        updatedItem.totalAmount = size * count;
    }
    
    updatedEvents[index] = updatedItem;
    setRecognizedEvents(updatedEvents);
  };

  const handleConfirmAll = async () => {
    toastService.success('Події успішно додано (імітація)');
    if (onRefreshDashboard) onRefreshDashboard();
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          borderRadius: '16px',
        }
      }}
      data-i-doc={ModalIndices.QUICK_FOOD_ADD}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FastfoodIcon sx={{ color: '#ff8f00' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Додати їжу</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={expertMode}
                onChange={(e) => {
                  const val = e.target.checked;
                  setExpertMode(val);
                  localStorage.setItem('food-modal-expert-mode', String(val));
                }}
                size="small"
                sx={{ '& .MuiSwitch-thumb': { bgcolor: expertMode ? '#6EB52C' : undefined }, '& .MuiSwitch-track': { bgcolor: expertMode ? '#6EB52C !important' : undefined } }}
              />
            }
            label={<Typography variant="caption" sx={{ fontWeight: 600, color: expertMode ? '#6EB52C' : 'text.secondary' }}>Режим експерта</Typography>}
            labelPlacement="start"
            sx={{ mr: 0, ml: 0 }}
          />
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f9f9f9', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            label="Опишіть ваші події..."
            placeholder="Наприклад: 2 бокали вина по 150мл о 19:00"
            variant="outlined"
            multiline
            rows={recognizedEvents.length > 0 ? 2 : 6}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isRecognizing || isEstimating}
            sx={{
              bgcolor: 'white',
              transition: 'all 0.3s ease',
              '& .MuiInputBase-root': {
                borderRadius: '12px',
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
            {recognizedEvents.length > 0 && (
              <Button
                onClick={handleEstimateNutrition}
                variant="outlined"
                disabled={isRecognizing || isEstimating || editingIndex !== null}
                startIcon={isEstimating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                sx={{ borderRadius: '10px', px: 3, color: '#6EB52C', borderColor: '#6EB52C' }}
              >
                {isEstimating ? 'Рахуємо...' : 'Розрахувати нутрієнти'}
              </Button>
            )}
            <Button 
              onClick={handleRecognize} 
              variant="contained" 
              disabled={isRecognizing || isEstimating || !inputText.trim()}
              startIcon={isRecognizing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ 
                backgroundColor: '#6EB52C', 
                borderRadius: '10px',
                px: 3,
                '&:hover': { backgroundColor: '#5da025' } 
              }}
            >
              {isRecognizing ? 'Розпізнаємо...' : (recognizedEvents.length > 0 ? 'Розпізнати заново' : 'Розпізнати')}
            </Button>
          </Box>
        </Box>

        {totals.calories > 0 && (
          <Fade in={totals.calories > 0}>
            <Paper sx={{ p: 2, mb: 1, bgcolor: '#E8F5E9', borderRadius: '12px', border: '1px solid #6EB52C' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#2E7D32', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                ЗАГАЛЬНИЙ ПІДСУМОК:
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>{totals.calories.toFixed(0)}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>ккал</Typography>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>{totals.protein.toFixed(1)}г</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Білки</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>{totals.fat.toFixed(1)}г</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Жири</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>{totals.carbs.toFixed(1)}г</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Вуглеводи</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1, color: '#2E7D32' }}>{totals.fiber.toFixed(1)}г</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#2E7D32' }}>Клітковина</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#282828', mb: 0 }}>
          Результати:
        </Typography>

        {recognizedEvents.length > 0 ? (
          <Fade in={recognizedEvents.length > 0}>
            <Box>
              <List disablePadding>
                {recognizedEvents.map((event, index) => {
                  const itemNutrition = calculateItemNutrients(Number(event.totalAmount), event.nutrition);

                  return (
                    <Paper 
                      key={index} 
                      variant="outlined" 
                      sx={{ 
                          mb: 1.5, 
                          borderRadius: '12px', 
                          overflow: 'hidden',
                          borderColor: editingIndex === index ? '#6EB52C' : '#e0e0e0',
                          borderWidth: editingIndex === index ? 2 : 1
                      }}
                    >
                      <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                            {getEventIcon(event.type)}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            {editingIndex === index ? (
                              <TextField 
                                  fullWidth
                                  size="small"
                                  label="Назва"
                                  value={event.name}
                                  onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                              />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                      {event.name || 'Без назви'}
                                  </Typography>
                                  {event.originalText && (
                                      <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                          <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                      </Tooltip>
                                  )}
                                  <Tooltip title={`Час: ${formatTime(event.scheduledLdt)}`}>
                                      <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                  </Tooltip>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button 
                              size="small" 
                              variant={editingIndex === index ? "contained" : "text"} 
                              color={editingIndex === index ? "success" : "inherit"}
                              onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                              startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                            >
                              {editingIndex === index ? 'Ок' : 'Змінити'}
                            </Button>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteItem(index)}
                              sx={{ opacity: 0.7 }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ width: '100%', ml: editingIndex === index ? 0 : 5 }}>
                          {editingIndex === index ? (
                            <Grid container spacing={1} sx={{ width: '100%' }}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary"><b>Тип:</b> {event.type === 'food' ? 'Їжа' : event.type === 'liquid' ? 'Напій' : 'Інше'}</Typography>
                                        <TextField 
                                            size="small" 
                                            label="Порція (назва)" 
                                            value={event.portionName || ''} 
                                            placeholder="бокал, тарілка..."
                                            onChange={(e) => handleUpdateEventField(index, 'portionName', e.target.value)}
                                        />
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            label="К-сть в порції" 
                                            value={event.portionSize || ''} 
                                            onChange={(e) => handleUpdateEventField(index, 'portionSize', e.target.value)}
                                        />
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body2"><b>Час:</b> {formatTime(event.scheduledLdt)}</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            label="К-сть порцій" 
                                            value={event.portionCount || ''} 
                                            onChange={(e) => handleUpdateEventField(index, 'portionCount', e.target.value)}
                                        />
                                        <Box sx={{ mt: 1, p: 1, bgcolor: '#E8F5E9', borderRadius: 1 }}>
                                            <Typography variant="body2"><b>Разом:</b> {event.totalAmount || 0} {event.unit}</Typography>
                                            {itemNutrition && (
                                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                                                {itemNutrition.kcal} ккал | Б: {itemNutrition.p}г Ж: {itemNutrition.f}г В: {itemNutrition.c}г К: {itemNutrition.fiber}г
                                              </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: event.nutrition ? 1 : 0 }}>
                                {(event.type === 'food' || event.type === 'liquid') && (
                                  <>
                                    {(event.portionCount || event.portionName) && (
                                      <Chip 
                                        label={`${event.portionCount || 1} ${event.portionName || 'порц.'} ${event.portionSize ? `(по ${event.portionSize} ${event.unit || ''})` : ''}`}
                                        size="small" 
                                        variant="outlined" 
                                        sx={{ 
                                          fontWeight: 600,
                                          borderColor: event.type === 'food' ? '#6EB52C' : '#41AEE4',
                                          color: event.type === 'food' ? '#6EB52C' : '#41AEE4',
                                          bgcolor: 'rgba(0,0,0,0.02)'
                                        }}
                                      />
                                    )}

                                    {event.totalAmount !== undefined && (
                                      <Chip 
                                        label={`Разом: ${event.totalAmount} ${event.unit || ''}`}
                                        size="small" 
                                        sx={{ 
                                          fontWeight: 600,
                                          bgcolor: event.type === 'food' ? '#E8F5E9' : '#E3F2FD',
                                          color: event.type === 'food' ? '#2E7D32' : '#1976D2',
                                        }}
                                      />
                                    )}
                                    
                                    {itemNutrition && (
                                      <Chip 
                                        label={`${itemNutrition.kcal} ккал`}
                                        size="small"
                                        sx={{ fontWeight: 700, bgcolor: '#6EB52C', color: '#fff' }}
                                      />
                                    )}
                                  </>
                                )}

                                {event.type === 'workout' && event.durationMinutes && (
                                  <Chip 
                                    label={`${event.durationMinutes} хв`} 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>

                              {itemNutrition && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700 }}>
                                    Всього: Б: {itemNutrition.p}г | Ж: {itemNutrition.f}г | В: {itemNutrition.c}г | К: {itemNutrition.fiber}г
                                  </Typography>
                                </Box>
                              )}

                              {event.nutrition && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: 1, 
                                    bgcolor: '#f5f5f5', 
                                    p: 0.8, 
                                    borderRadius: '6px',
                                    borderLeft: '3px solid #ccc'
                                }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span style={{ fontSize: '0.7rem' }}>БАЗА (на 100г/мл):</span>
                                        <span><b>Кал:</b> {event.nutrition.caloriesPer100}</span>
                                        <span><b>Б:</b> {event.nutrition.proteinPer100}</span>
                                        <span><b>Ж:</b> {event.nutrition.fatPer100}</span>
                                        <span><b>В:</b> {event.nutrition.carbsPer100}</span>
                                        <span><b>К:</b> {event.nutrition.fiberPer100}</span>
                                    </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    </Paper>
                  );
                })}
              </List>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
                <Button 
                  startIcon={<AddIcon />} 
                  variant="text"
                  onClick={handleAddItemManual}
                  sx={{ color: '#6EB52C', fontWeight: 700 }}
                >
                  Додати страву вручну
                </Button>
              </Box>
            </Box>
          </Fade>
        ) : (
          !isRecognizing && inputText.trim() && (
            <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography color="textSecondary">Результати відсутні. Додайте страву вручну або натисніть "Розпізнати".</Typography>
              <Button 
                startIcon={<AddIcon />} 
                variant="outlined"
                onClick={handleAddItemManual}
                sx={{ color: '#6EB52C', borderColor: '#6EB52C' }}
              >
                Додати страву вручну
              </Button>
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9', justifyContent: 'space-between' }}>
        <Box>
          {recognizedEvents.length > 0 && (
            <Button
              onClick={handleGenerateReport}
              variant="text"
              startIcon={<DescriptionIcon />}
              sx={{ color: '#1976d2' }}
            >
              Згенерувати звіт
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} color="inherit" disabled={isRecognizing || isEstimating}>
            Скасувати
          </Button>
          <Button 
            onClick={handleConfirmAll}
            variant="contained" 
            disabled={recognizedEvents.length === 0 || isRecognizing || isEstimating || editingIndex !== null}
            sx={{ 
              backgroundColor: '#6EB52C', 
              borderRadius: '10px',
              px: 4,
              '&:hover': { backgroundColor: '#5da025' } 
            }}
          >
            Підтвердити та зберегти
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
